
import { useState } from 'react';
import Navbar from '../../components/Navbar';

interface Meeting {
  id: number;
  title: string;
  date: string;
  time: string;
  location: string;
  agenda: string;
  attendees: number;
  status: 'upcoming' | 'completed';
}

export default function MeetingsPage() {
  const [rsvpStatus, setRsvpStatus] = useState<{ [key: number]: boolean }>({});
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  const meetings: Meeting[] = [
    {
      id: 1,
      title: 'Monthly Board Meeting',
      date: '2025-02-15',
      time: '7:00 PM',
      location: 'Community Center',
      agenda: 'Budget review, landscaping updates, pool maintenance discussion',
      attendees: 24,
      status: 'upcoming'
    },
    {
      id: 2,
      title: 'Annual General Meeting',
      date: '2025-03-20',
      time: '6:30 PM',
      location: 'Community Center',
      agenda: 'Annual report, board elections, community improvements',
      attendees: 45,
      status: 'upcoming'
    },
    {
      id: 3,
      title: 'Emergency Board Meeting',
      date: '2025-01-10',
      time: '7:00 PM',
      location: 'Community Center',
      agenda: 'Storm damage assessment and repair planning',
      attendees: 18,
      status: 'completed'
    }
  ];

  const handleRSVP = (meetingId: number, meetingTitle: string) => {
    setRsvpStatus({ ...rsvpStatus, [meetingId]: true });
    setNotificationMessage(`✓ You're registered for "${meetingTitle}"!`);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const upcomingMeetings = meetings.filter(m => m.status === 'upcoming');
  const pastMeetings = meetings.filter(m => m.status === 'completed');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar />

      {/* Success Notification */}
      {showNotification && (
        <div className="fixed top-24 right-6 z-50 animate-[slideInRight_0.3s_ease-out]">
          <div className="bg-teal-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
            <i className="ri-checkbox-circle-fill text-2xl"></i>
            <span className="font-medium">{notificationMessage}</span>
          </div>
        </div>
      )}

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="mb-12 animate-[fadeIn_0.5s_ease-out]">
            <h1 className="text-5xl font-bold text-slate-900 mb-4">Community Meetings</h1>
            <p className="text-xl text-slate-600">Stay informed and participate in your HOA meetings</p>
          </div>

          {/* Upcoming Meetings */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 flex items-center gap-3">
              <i className="ri-calendar-event-fill text-teal-500"></i>
              Upcoming Meetings
            </h2>
            <div className="grid gap-6">
              {upcomingMeetings.map((meeting, index) => (
                <div 
                  key={meeting.id} 
                  className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-100 animate-[slideUp_0.5s_ease-out]"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-slate-900 mb-3">{meeting.title}</h3>
                        <div className="flex flex-wrap gap-4 text-slate-600">
                          <div className="flex items-center gap-2">
                            <i className="ri-calendar-line text-teal-500"></i>
                            <span>{new Date(meeting.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <i className="ri-time-line text-teal-500"></i>
                            <span>{meeting.time}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <i className="ri-map-pin-line text-teal-500"></i>
                            <span>{meeting.location}</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-teal-50 px-4 py-2 rounded-full">
                        <span className="text-teal-700 font-semibold flex items-center gap-2">
                          <i className="ri-user-line"></i>
                          {meeting.attendees} attending
                        </span>
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                        <i className="ri-file-list-line text-teal-500"></i>
                        Agenda
                      </h4>
                      <p className="text-slate-600 leading-relaxed">{meeting.agenda}</p>
                    </div>

                    <div className="flex gap-3">
                      {rsvpStatus[meeting.id] ? (
                        <button 
                          disabled
                          className="px-6 py-3 bg-teal-500 text-white rounded-lg font-semibold flex items-center gap-2 cursor-not-allowed opacity-75"
                        >
                          <i className="ri-checkbox-circle-fill"></i>
                          Registered
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleRSVP(meeting.id, meeting.title)}
                          className="px-6 py-3 bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-600 transition-all duration-200 flex items-center gap-2 hover:scale-105 whitespace-nowrap"
                        >
                          <i className="ri-calendar-check-line"></i>
                          RSVP Now
                        </button>
                      )}
                      <button className="px-6 py-3 border-2 border-slate-200 text-slate-700 rounded-lg font-semibold hover:border-teal-500 hover:text-teal-500 transition-all duration-200 flex items-center gap-2 whitespace-nowrap">
                        <i className="ri-download-line"></i>
                        Download Agenda
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Past Meetings */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-8 flex items-center gap-3">
              <i className="ri-history-line text-slate-500"></i>
              Past Meetings
            </h2>
            <div className="grid gap-6">
              {pastMeetings.map((meeting, index) => (
                <div 
                  key={meeting.id} 
                  className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-slate-100 opacity-75 hover:opacity-100 animate-[slideUp_0.5s_ease-out]"
                  style={{ animationDelay: `${(upcomingMeetings.length + index) * 0.1}s` }}
                >
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-slate-700 mb-3">{meeting.title}</h3>
                        <div className="flex flex-wrap gap-4 text-slate-500">
                          <div className="flex items-center gap-2">
                            <i className="ri-calendar-line"></i>
                            <span>{new Date(meeting.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <i className="ri-user-line"></i>
                            <span>{meeting.attendees} attended</span>
                          </div>
                        </div>
                      </div>
                      <span className="bg-slate-100 text-slate-600 px-4 py-2 rounded-full font-semibold text-sm">
                        Completed
                      </span>
                    </div>

                    <button className="px-6 py-3 border-2 border-slate-200 text-slate-700 rounded-lg font-semibold hover:border-slate-400 transition-all duration-200 flex items-center gap-2 whitespace-nowrap">
                      <i className="ri-file-text-line"></i>
                      View Minutes
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
